<template>
  <div class="login_box box">
    <form id="loginform" role="form" method="post" @submit.prevent="login" aria-label="Login form">
      <h2 style="margin-top: 0">Sign in</h2>

      <slot></slot>

      <div v-if="shared_user && !shared_user.has_password" class="alert alert-info">
        <p style="margin-top: 0">
          Welcome <strong>{{ shared_user.FirstName }}</strong>! It looks like you haven't set a password yet.
        </p>
        <p>
          <router-link :to="signupLinkWithSharedUser">Click here to create your account</router-link> and set your password.
        </p>
      </div>

      <Messages :messages="messages" />
      <template v-if="!messages || messages.length == 0">
        <div v-if="error == 409 || error == 7" class="alert alert-danger">
          <p style="margin-top: 0; font-weight: bold">
            You have been disconnected because someone else is using your account.
          </p>
          <p>Only one user can be connected with this email.</p>
          <p>
            If you don't know why this message appears, please change your password quickly. Your account may have been
            compromised.
          </p>
        </div>
        <div v-else-if="error == 401 || error == 8" class="alert alert-danger">
          Please, sign in to access this page.
        </div>
      </template>

      <div>
        <label for="Email">Email:</label>
        <input
          type="text"
          class="form-control"
          :class="{ 'readonly-input': shared_user }"
          placeholder="Email"
          autocomplete="username"
          name="Email"
          id="Email"
          v-model="email"
          :readonly="!!shared_user"
          required
          autofocus
        />
      </div>

      <div style="margin-top: 2em">
        <label for="pass">Password:</label>
        <PasswordInput
          name="pass"
          :ignore_strength="true"
          :password="password"
          @change="set_password($event)"
          placeholder="Password"
        >
        </PasswordInput>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: baseline; margin-top: 2em">
        <router-link :to="signupLinkWithSharedUser">Create an account</router-link>
        <div>
          <a @click.prevent="password_recall">Lost password ?</a>
          <input type="hidden" name="site" value="app" />
          <ButtonWithIndicator style="margin-left: 1em" btn_style="primary" :show_indicator="loading" type="submit"
            >Sign in</ButtonWithIndicator
          >
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import router from "@/router/index";
import Messages from "@/components/Messages.vue";
import PasswordInput from "@/components/PasswordInput.vue";
import ButtonWithIndicator from "@/components/ButtonWithIndicator.vue";
import { ApiMessage, WSClient } from "@/ems/WSClient";
import { userSessionStore } from "@/store/user_session";
import { LoggedUser } from "@/types/user";
import { RouteLocationNamedRaw } from "vue-router";

export interface SharedUser {
  user_id: string;
  email_hash: string;
  Email: string;
  FirstName: string;
  has_password: boolean;
}

const user_session = userSessionStore();

const props = defineProps<{
  initial_messages?: ApiMessage[];
  initial_email?: string;
  redir_on_success?: string;
  error?: number;
  shared_user?: SharedUser | null;
}>();

const signupLinkWithSharedUser = computed(() => {
  if (props.shared_user) {
    const link: RouteLocationNamedRaw = {
      name: "signup_shared",
      params: {
        user_id: props.shared_user.user_id,
        email_hash: props.shared_user.email_hash,
      },
    };
    // Preserve the redir query parameter
    if (props.redir_on_success) {
      link.query = { redir: props.redir_on_success };
    }
    return link;
  }
  // For regular signup, also preserve redir if present
  const link: RouteLocationNamedRaw = { name: "signup" };
  if (props.redir_on_success) {
    link.query = { redir: props.redir_on_success };
  }
  return link;
});

const email = ref(""),
  password = ref(""),
  loading = ref(false),
  messages = ref<ApiMessage[]>([]),
  client = new WSClient(loading, messages);

const login = async () => {
    user_session.reset_logout();

    if (!email.value) {
      messages.value = [{ level: "error", text: "Email required" }];
      return;
    }
    if (!password.value) {
      messages.value = [{ level: "error", text: "Password required" }];
      return;
    }
    messages.value = [];

    const login_resp = await client.queryWs<{ jwt: string; logged_user: LoggedUser }>("POST", "session_login", null, {
      login: email.value,
      pass: password.value,
    });
    if (client.status_code === undefined || (client.status_code < 200 && client.status_code >= 300)) {
      return;
    }
    user_session.on_auth_success(login_resp.jwt, login_resp.logged_user);

    const ug = (<any>window).userGuiding;
    if (ug && user_session.logged_user?.UserId)
      ug.identify(user_session.logged_user.UserId, {
        email: email.value,
      });
    let new_route: RouteLocationNamedRaw = { name: "SeriesList", query: { action: "login" } };
    const vueRedir = sessionStorage.getItem("afterlogin");
    if (vueRedir) {
      try {
        const parsed_route = JSON.parse(vueRedir);
        parsed_route["query"] = { ...parsed_route?.query, action: "login" };
        new_route = parsed_route;
      } catch (e) {
        console.error("Error parsing vueRedir", vueRedir, e);
      }
    }
    try {
      await router.push(new_route);
    } catch (e) {
      console.error("Error navigating to saved route, falling back to SeriesList", e);
      sessionStorage.removeItem("afterlogin");
      await router.push({ name: "SeriesList", query: { action: "login" } });
    }
  },
  password_recall = () => {
    router.push({ name: "ask_password", params: { email: email.value } });
  },
  set_password = (inStr: string) => {
    password.value = inStr;
  };

watch(
  () => props.redir_on_success,
  (redir) => {
    if (redir == "reset") {
      sessionStorage.removeItem("afterlogin");
    }
  },
  { immediate: true }
);

watch(
  () => props.initial_messages,
  (init_messages) => {
    messages.value = init_messages ?? [];
  },
  { immediate: true }
);

watch(
  () => props.initial_email,
  (mail) => {
    if (mail) email.value = mail;
  },
  { immediate: true }
);

onMounted(() => {});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.login_box {
  margin: 7.5rem auto;
  max-width: 45em;
  padding: 4em 5em;
  text-align: left;
}

h2 {
  font-size: 2.14em;
}

label {
  display: inline-block;
  margin-bottom: 5px;
  font-weight: bold;
}

.password {
  background-color: transparent;
  border: 0;
  flex-grow: 1;
  padding: 0.375rem 0.75rem;
  font-size: 1em;
}

.readonly-input {
  background-color: #e9ecef;
  cursor: not-allowed;
}
</style>
